import React, { useEffect, useState, lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import orderBy from 'lodash/orderBy';
import uniqBy from 'lodash/uniqBy';
import uniq from 'lodash/uniq';
import { injectIntl, defineMessages } from 'react-intl';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Container } from 'reactstrap';

import { getFacets } from 'Redux/ducks/facet';
import FacetBreadcrumbs from 'Components/Utility/SEO/FacetBreadcrumbs';
import FacetCharacterIndex from 'Components/FacetSearch/FacetCharacterIndex';
import FacetList from 'Components/Utility/SEO/FacetList';
import FranchiseImgGrid from 'Components/FacetSearch/FranchiseImgGrid';
import languageStrings from 'Components/FacetSearch/LanguagesStrings';
import Meta from 'Components/Utility/SEO/Meta';
import PublisherCarousel from 'Components/HomePage/PublisherCarousel';

import 'Components/FacetSearch/FacetOrderedList.scss';

const ProductFinder = lazy(() => import(/* webpackChunkName: "ProductFinder" */ 'Components/ProductFinder/ProductFinder'));

// TODO Nested lists for Categories

const FacetOrderedList = ({ intl: { formatMessage }, match, location }) => {
  const [facets, setFacets] = useState([]);
  const [fetchError, setFetchError] = useState(null);

  const facet = match.params.facet;

  useEffect(() => {
    const fetchFacets = async () => {
      const { data } = await getFacets(facet);
      setFacets(uniqBy(orderBy(data, ['slug'], ['asc']), 'slug'));
    };

    fetchFacets().catch(setFetchError);
  }, [facet]);

  useEffect(() => {
    const hash = location.hash.replace('#', '');

    if (hash) {
      setTimeout(() => {
        if (document.getElementById(hash)) {
          document.getElementById(hash).scrollIntoView({ behavior: 'smooth' });
        }
      }, 300);
    }
  }, [location]);

  if (fetchError) {
    throw fetchError;
  }

  const messages = {
    publishers: defineMessages({
      seotitle: {
        id: 'facetorderedlist.publishers.seotitle',
        defaultMessage: 'Officially Licensed Publishers',
      },
      title: {
        id: 'facetorderedlist.publishers.title',
        defaultMessage: 'Our Officially Licensed Publishers',
      },
      seodesc: {
        id: 'facetorderedlist.publishers.seodesc',
        defaultMessage: 'Discover officially licensed PC video games from over 1100+ publishing partners.',
      },
      desc: {
        id: 'facetorderedlist.publishers.desc',
        defaultMessage: 'The developer creates the game, the publisher sells it! From small family-run businesses to large conglomerates and global pioneers, game publishers play a vital role in ensuring your favorite PC games hit the market. Through investing, marketing, and many other key factors, publishers help the best game devs and their projects thrive - and that’s why you can find so many fantastic games right here at Fanatical from some of the biggest and most popular publishers around. Remember, we only work with officially licensed publishers - meaning your purchases help support the teams behind these awesome PC titles!',
      },
    }),
    'games-like': defineMessages({
      seotitle: {
        id: 'facetorderedlist.games-like.seotitle',
        defaultMessage: 'Games Like',
      },
      title: {
        id: 'facetorderedlist.games-like.title',
        defaultMessage: 'Discover games like your favourites',
      },
      seodesc: {
        id: 'facetorderedlist.games-like.seodesc',
        defaultMessage: 'Find more officially licensed PC video games like your favourites.',
      },
    }),
    franchises: defineMessages({
      seotitle: {
        id: 'facetorderedlist.franchises.seotitle',
        defaultMessage: 'Browse Games by Franchise',
      },
      title: {
        id: 'facetorderedlist.franchises.title',
        defaultMessage: 'Browse Games by Franchise',
      },
      seodesc: {
        id: 'facetorderedlist.franchises.seodesc',
        defaultMessage: 'Discover all the top PC video game franchises available from Fanatical',
      },
      desc: {
        id: 'facetorderedlist.franchises.desc',
        defaultMessage: 'Memorable games make a franchise even more special. Over the years, there have been an incredible collection of PC titles launched from the biggest publishers on the planet - selling millions of copies and scooping multiple awards to become household names among gamers - building excitement for future sequels and expansions. Whether you’re looking to discover the next big AAA games or hoping to find an exciting selection of Indie alternatives, we have a huge range of franchises to choose from right here!',
      },
    }),
    collections: defineMessages({
      title: {
        id: 'facet.collections.title',
        defaultMessage: 'Our Collections',
      },
      seodesc: {
        id: 'facet.collections.seodesc',
        defaultMessage: 'Browse our hand-curated collections of PC, Mac, and Linux games',
      },
    }),
    categories: defineMessages({
      title: {
        id: 'facet.categories.seotitle',
        defaultMessage: 'Genres, Themes & Playstyles',
      },
      seodesc: {
        id: 'facet.categories.seodesc',
        defaultMessage: 'Whatever type of gamer you are we\'ve got you covered. From shooters to strategy and indie to RPG, we are Fanatical about always bringing you the best PC, Mac, and Linux game deals on the planet.',
      },
    }),
  };

  const letterRegex = /[a-zA-Z ]+/;

  const seotitle = `${formatMessage(messages[facet].seotitle || messages[facet].title)} | Fanatical`;
  const title = formatMessage(messages[facet].title);
  const desc = formatMessage(messages[facet].seodesc || languageStrings.desc);

  const facetNames = facets.map(f => f.name);
  const allFacetCharacters = facetNames.map(f => f.charAt(0).toUpperCase());
  const facetLetterCharacters = allFacetCharacters.filter(f => letterRegex.test(f));
  const facetNonLetterCharacter = allFacetCharacters.filter(f => !letterRegex.test(f));
  const facetFirstCharacterIndex = uniq(facetLetterCharacters).sort();
  const facetCharacterGroups = {};

  if (!facetFirstCharacterIndex.length) {
    return false;
  }

  if (facetNonLetterCharacter.length > 0) {
    facetFirstCharacterIndex.unshift('0-9');
  }

  facetFirstCharacterIndex.forEach((character) => {
    let facetCharacterMatches = [];

    facetCharacterMatches = facets.filter(f => f.name.charAt(0).toUpperCase() === character);

    if (character === '0-9') {
      facetCharacterMatches = facets.filter(f => !letterRegex.test(f.name.charAt(0)));
    }

    facetCharacterGroups[character.toUpperCase()] = facetCharacterMatches;
  });

  let displayCharacters = true;

  const excludeLocales = ['ja', 'ru', 'ko', 'zh-hans', 'zh-hant'];
  if (excludeLocales.includes(window.locale)) {
    displayCharacters = false;
  }

  return (
    <div className="facet-ordered-list-page">
      <Meta
        title={seotitle}
        description={desc}
        url={facet}
        pageType="CollectionPage"
      />
      <Container>
        <FacetList isMainEntity facet={facet} facets={facets} />
        <FacetBreadcrumbs facet={facet} />
        <h1 className="pt-5 pb-4">{title}</h1>

        <p className="facet-message paragraph-small">
          {formatMessage(messages[facet].desc || messages[facet].seodesc)}
        </p>
      </Container>


      {facet === 'publishers' &&
        <PublisherCarousel showDealNumbers lazyLoad={false} />
      }

      <Container>
        {facet === 'franchises' &&
          <FranchiseImgGrid />
        }

        {displayCharacters &&
          <div className="facet-character-index-container">
            <FacetCharacterIndex characterIndex={facetFirstCharacterIndex} />
            <div className="right-gradient" />
          </div>
        }

        <ol className="facet-ordered-list-container">
          {displayCharacters &&
            <React.Fragment>
              {facetFirstCharacterIndex.map(character =>
                <li className="facet-character-row" key={character} id={character.toLowerCase()}>
                  <div className="facet-character">
                    {character}
                  </div>
                  <ol className="facet-character-list">
                    {facetCharacterGroups[character].map(item =>
                      <li key={item.name} className="facet-list-item">
                        <Link to={`/${item.facet === 'combo' ? 'categories' : facet}/${item.slug}`}>
                          {facet === 'games-like' ? formatMessage(languageStrings.gamesLikeTitle, { gameName: item.name }) : item.name}
                        </Link>
                      </li>,
                    )}
                  </ol>
                </li>,
              )}
            </React.Fragment>
          }
          {!displayCharacters &&
            <li className="facet-character-row">
              <ol className="facet-character-list">
                {facets.map(item =>
                  <li key={item.name} className="facet-list-item">
                    <Link to={`/${item.facet === 'combo' ? 'categories' : facet}/${item.slug}`}>
                      {facet === 'games-like' ? formatMessage(languageStrings.gamesLikeTitle, { gameName: item.name }) : item.name}
                    </Link>
                  </li>,
                )}
              </ol>
            </li>
          }
        </ol>
      </Container>

      <Suspense>
        <ProductFinder />
      </Suspense>
    </div>
  );
};

FacetOrderedList.propTypes = {
  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
  }).isRequired,
  match: PropTypes.shape({}).isRequired,
  location: PropTypes.shape({
    hash: PropTypes.string.isRequired,
  }).isRequired,
};

const mapStateToProps = ({
  auth, steam, router: { location }, cart: { items },
}) => ({
  auth, steam, location, items,
});

export default connect(mapStateToProps, null)(injectIntl(FacetOrderedList));
